import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, absUrl } from "@/lib/api";

const LOGO_FULL = "/bloomers-hero-logo.png";

export default function HeroAnimation() {
  const reduce = useReducedMotion();
  const [carUrl, setCarUrl] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [carReady, setCarReady] = useState(false); // set to true only when the uploaded image is decoded
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setCarUrl(r.data?.hero_car_url || null))
      .catch(() => setCarUrl(null))
      .finally(() => setSettingsLoaded(true));
  }, []);

  // Preload the uploaded car image so it's fully decoded before we animate.
  // No placeholder is rendered — if there is no uploaded image, nothing shows.
  useEffect(() => {
    if (!settingsLoaded) return;
    if (!carUrl) {
      setCarReady(false);
      return;
    }
    setCarReady(false);
    const img = new Image();
    img.onload = () => {
      setCarReady(true);
      setRunKey((k) => k + 1);
    };
    img.onerror = () => {
      // If load fails, keep car hidden — no placeholder.
      setCarReady(false);
    };
    img.src = absUrl(carUrl);
  }, [carUrl, settingsLoaded]);

  // Ruby car left-to-right slide is CSS-driven via .ruby-slide (see index.css)

  const showCar = Boolean(carUrl) && carReady;

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden hero-sky"
    >
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#21C078]/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-24 w-[520px] h-[520px] rounded-full bg-[#C0AD21]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-8 sm:pt-24 sm:pb-14 min-h-[92vh] flex flex-col justify-between">
        <div className="mt-10 sm:mt-16 grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: text */}
          <div className="order-2 lg:order-1">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight"
            >
              A garden that
              <br />
              <span className="text-[#21C078]">grows with you</span>
              <span className="text-[#C0AD21]">.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.7 }}
              className="mt-6 max-w-xl text-lg text-[#1C1C1C]/75 leading-relaxed"
            >
              Bloomers is a local woman-owned plantscape design & installation
              studio for proud LKN homeowners. Affordable. Professional.
              Deeply rooted in the details.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-pill-primary">
                Consult Inquiry <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/gallery" data-testid="hero-cta-secondary" className="btn-pill-ghost">
                See recent projects
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.65 }}
              className="mt-10 flex items-center gap-6 text-sm text-[#1C1C1C]/60"
            >
              <div>
                <div className="text-2xl font-black text-[#1C1C1C]">15+</div>
                <div>years in LKN</div>
              </div>
              <div className="h-8 w-px bg-[#1C1C1C]/10" />
              <div>
                <div className="text-2xl font-black text-[#1C1C1C]">3-step</div>
                <div>design process</div>
              </div>
              <div className="h-8 w-px bg-[#1C1C1C]/10" />
              <div>
                <div className="text-2xl font-black text-[#1C1C1C]">100%</div>
                <div>hand-selected plants</div>
              </div>
            </motion.div>
          </div>

          {/* Right: Hero stage — logo in top zone (fades in after car parks), Ruby drives in the bottom zone */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative flex flex-col h-[360px] sm:h-[440px] lg:h-[540px] rounded-3xl overflow-visible">
              {/* Logo — top zone, fades in AFTER Ruby has parked */}
              <div
                className="flex items-center justify-center px-4 sm:px-6 shrink-0 basis-[55%] min-h-0"
                data-testid="hero-logo-reveal"
              >
                <div className="relative inline-block max-h-full max-w-full">
                  <img
                    src={LOGO_FULL}
                    alt="Bloomers — Inspired Plantscapes"
                    className="logo-fade-in max-h-full w-auto max-w-full object-contain"
                  />
                  <span
                    className="logo-fade-in absolute text-[#5CA426] font-black text-xs sm:text-sm md:text-base leading-none select-none"
                    style={{ top: "59%", right: "2%" }}
                    aria-label="trademark"
                    data-testid="bloomers-tm"
                  >
                    ™
                  </span>
                </div>
              </div>

              {/* Car track — bottom zone; Ruby parks here, physically below the logo */}
              <div className="relative flex-1 min-h-0">
                {showCar && (
                  <div className="absolute inset-x-0 bottom-3 sm:bottom-4 flex justify-center pointer-events-none">
                    <img
                      key={`${carUrl}-${runKey}`}
                      data-testid="hero-ruby-car"
                      src={absUrl(carUrl)}
                      alt="Ruby the Bloomers car"
                      className="ruby-drive w-auto max-h-[195px] sm:max-h-[255px] lg:max-h-[315px] max-w-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-10 text-xs uppercase tracking-[0.25em] text-[#1C1C1C]/50 hidden sm:block"
        >
          — scroll to explore —
        </motion.div>
      </div>
    </section>
  );
}
